import {
  UnixUser,
  type Server,
  type TerminalAuth,
  type TerminalHost,
} from "@cloudserver/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowLeft, Search, Settings2, ShieldCheck } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../api/queries.js";
import { useServers } from "../api/servers.js";
import { useTerminalHosts } from "../api/terminal.js";
import { StatusLabel } from "../pages/server/status.js";
import { ErrorMessage } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { OsIcon } from "../ui/os-icon.js";
import { Dialog } from "../ui/overlay.js";
import { Button, Choice, Select, Spinner, TextField } from "../ui/primitives.js";
import { KindIcon } from "../ui/session.js";
import { openConsole } from "../pages/server/console/connection.js";
import { openRdp } from "../rdp/connection.js";
import {
  rdpCredentials,
  rdpFormValid,
  RdpReachabilityNote,
  RdpSignInFields,
  type RdpForm,
} from "../rdp/sign-in.js";
import { isWindowsServer } from "../ui/os.js";
import { openTerminal } from "./connections.js";
import { pickerChoice } from "./direct.js";
import {
  rememberedRdpUser,
  rememberedUser,
  serverBlock,
  terminals,
  type PickerRequest,
  type SessionKind,
} from "./store.js";

function matches(server: Server, text: string): boolean {
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return [server.name, server.publicIpv4?.address, server.publicIpv6?.network].some((v) =>
    v?.toLowerCase().includes(needle),
  );
}

/** Opens the session, closes the picker and shows the workspace. */
function useStart(projectId: string) {
  const navigate = useNavigate();
  return (server: Server, unixUser: string, auth: TerminalAuth, password?: string) => {
    openTerminal({
      projectId,
      server: {
        id: server.id,
        name: server.name,
        osFlavor: server.image?.osFlavor ?? null,
        osVersion: server.image?.osVersion ?? null,
      },
      unixUser,
      auth,
      ...(auth === "password" && password ? { password } : {}),
    });
    terminals.closePicker();
    void navigate({ to: "/terminals" });
  };
}

function ServerList({
  projectId,
  hosts,
  onChoose,
}: {
  projectId: string;
  hosts: readonly TerminalHost[] | undefined;
  /** The sign-in step of a server: its connection settings. */
  onChoose: (server: Server) => void;
}) {
  const { t } = useTranslation();
  const servers = useServers(projectId);
  const start = useStart(projectId);
  const [text, setText] = useState("");
  const rows = useMemo(
    () => (servers.data ?? []).filter((s) => matches(s, text)),
    [servers.data, text],
  );
  if (servers.isPending) return <Spinner label={t("common.loading")} />;
  if (servers.error) return <ErrorMessage error={servers.error} />;
  return (
    <div className="flex flex-col gap-3">
      <TextField
        type="search"
        hideLabel
        label={t("terminal.picker.search")}
        placeholder={t("terminal.picker.searchPlaceholder")}
        icon={<Search className="size-4" aria-hidden />}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      {servers.data.length === 0 ? (
        <p className="text-sm text-muted">{t("terminal.picker.noServers")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted">{t("terminal.picker.noMatch")}</p>
      ) : (
        <ul className="flex flex-col gap-1" aria-label={t("terminal.picker.servers")}>
          {rows.map((server) => {
            const choice = pickerChoice(server, hosts, rememberedUser);
            const blocked = choice.kind === "blocked";
            const address =
              [server.publicIpv4?.address, server.publicIpv6?.network]
                .filter(Boolean)
                .join(" · ") || t("terminal.picker.noAddress");
            return (
              <li
                key={server.id}
                className={clsx(
                  "flex items-stretch rounded-lg border border-line",
                  blocked && "opacity-60",
                )}
              >
                <button
                  type="button"
                  disabled={blocked}
                  onClick={() =>
                    choice.kind === "direct"
                      ? start(server, choice.unixUser, "certificate")
                      : onChoose(server)
                  }
                  className={clsx(
                    "flex min-w-0 flex-1 items-center gap-3 rounded-lg px-3 py-2 text-left",
                    blocked ? "cursor-not-allowed" : "hover:bg-surface-2",
                  )}
                >
                  <OsIcon
                    flavor={server.image?.osFlavor}
                    version={server.image?.osVersion}
                    description={server.image?.description}
                    variant="brand"
                    className="size-6"
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium" title={server.name}>
                      {server.name}
                    </span>
                    <span className="truncate font-mono text-xs text-muted" title={address}>
                      {address}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                    <StatusLabel status={server.status} />
                    {choice.kind === "blocked" && (
                      <span className="text-muted">{t(`terminal.picker.${choice.reason}`)}</span>
                    )}
                    {choice.kind === "direct" && (
                      <span className="flex items-center gap-1 text-muted">
                        <ShieldCheck className="size-3.5 text-ok" aria-hidden />
                        {t("terminal.picker.direct", { user: choice.unixUser })}
                      </span>
                    )}
                  </span>
                </button>
                {!blocked && (
                  <button
                    type="button"
                    onClick={() => onChoose(server)}
                    aria-label={t("terminal.picker.settings", { name: server.name })}
                    title={t("terminal.picker.settings", { name: server.name })}
                    className="flex w-10 shrink-0 items-center justify-center rounded-r-lg border-l border-line text-muted hover:bg-surface-2 hover:text-text"
                  >
                    <Settings2 className="size-4" aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Whether the server takes the certificate, and where to set it up. */
function CertificateState({
  projectId,
  server,
  workedAt,
}: {
  projectId: string;
  server: Server;
  workedAt: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  if (workedAt) {
    return (
      <p className="flex items-start gap-2 text-sm text-muted">
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden />
        {t("terminal.picker.certificateWorks", { time: relativeTime(workedAt, i18n.language) })}
      </p>
    );
  }
  return (
    <p className="text-sm text-muted">
      {t("terminal.picker.certificateUnknown")}{" "}
      <Link
        to="/projects/$projectId/servers/$serverId"
        params={{ projectId, serverId: server.id }}
        onClick={() => terminals.closePicker()}
        className="text-accent-strong underline underline-offset-2"
      >
        {t("terminal.picker.setupLink")}
      </Link>
    </p>
  );
}

function SignInStep({
  projectId,
  server,
  workedAt,
  onBack,
}: {
  projectId: string;
  server: Server;
  workedAt: string | null | undefined;
  onBack: (() => void) | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const start = useStart(projectId);
  const [kind, setKind] = useState<SessionKind>(() => (isWindowsServer(server) ? "rdp" : "ssh"));
  const [user, setUser] = useState(() => rememberedUser(server.id));
  const [auth, setAuth] = useState<TerminalAuth>("certificate");
  const [password, setPassword] = useState("");
  const [rdp, setRdp] = useState<RdpForm>(() => ({
    username: rememberedRdpUser(server.id),
    password: "",
    domain: "",
  }));
  const valid = UnixUser.safeParse(user).success;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (kind === "vnc") {
      openConsole({
        projectId,
        server: {
          id: server.id,
          name: server.name,
          osFlavor: server.image?.osFlavor ?? null,
          osVersion: server.image?.osVersion ?? null,
        },
      });
      terminals.closePicker();
      void navigate({ to: "/terminals" });
      return;
    }
    if (kind === "rdp") {
      if (!rdpFormValid(rdp)) return;
      openRdp({
        projectId,
        server: { id: server.id, name: server.name },
        credentials: rdpCredentials(rdp),
      });
      setRdp({ ...rdp, password: "" });
      terminals.closePicker();
      void navigate({ to: "/terminals" });
      return;
    }
    if (!valid || (auth === "password" && !password)) return;
    start(server, user.trim(), auth, password);
    setPassword("");
  };
  return (
    <form id="terminal-picker" onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex items-center gap-3 rounded-lg border border-line px-3 py-2">
        <OsIcon
          flavor={server.image?.osFlavor}
          version={server.image?.osVersion}
          description={server.image?.description}
          variant="brand"
          className="size-6"
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium" title={server.name}>
            {server.name}
          </span>
          <span
            className="truncate font-mono text-xs text-muted"
            title={server.publicIpv4?.address ?? server.publicIpv6?.network}
          >
            {server.publicIpv4?.address ?? server.publicIpv6?.network}
          </span>
        </span>
        {onBack && (
          <Button
            variant="ghost"
            icon={<ArrowLeft className="size-4" aria-hidden />}
            onClick={onBack}
          >
            {t("terminal.picker.otherServer")}
          </Button>
        )}
      </div>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="pb-1.5 text-sm font-medium">{t("rdp.picker.kind")}</legend>
        {(["ssh", "rdp", "vnc"] as const).map((option) => (
          <Choice
            key={option}
            name="session-kind"
            checked={kind === option}
            onChange={() => setKind(option)}
            hint={t(`rdp.picker.${option}Hint`)}
            icon={<KindIcon kind={option} />}
          >
            {t(`rdp.picker.${option}`)}
          </Choice>
        ))}
      </fieldset>
      {kind === "vnc" ? (
        <p className="text-sm text-muted">{t("rdp.picker.vncNote")}</p>
      ) : kind === "rdp" ? (
        <>
          <RdpSignInFields form={rdp} onChange={setRdp} />
          <RdpReachabilityNote projectId={projectId} serverId={server.id} />
        </>
      ) : (
        <>
          <TextField
            label={t("terminal.picker.user")}
            hint={t("terminal.picker.userHint")}
            value={user}
            onChange={(event) => setUser(event.target.value)}
            autoCapitalize="off"
            autoComplete="off"
            spellCheck={false}
            error={valid ? undefined : t("terminal.picker.userInvalid")}
          />
          <fieldset className="flex flex-col gap-1.5">
            <legend className="pb-1.5 text-sm font-medium">{t("terminal.picker.auth")}</legend>
            {(["certificate", "password"] as const).map((mode) => (
              <Choice
                key={mode}
                name="terminal-auth"
                checked={auth === mode}
                onChange={() => setAuth(mode)}
                hint={t(`terminal.picker.${mode}Hint`)}
              >
                {t(`terminal.picker.${mode}`)}
              </Choice>
            ))}
          </fieldset>
          {auth === "certificate" && (
            <CertificateState projectId={projectId} server={server} workedAt={workedAt} />
          )}
          {auth === "password" && (
            <TextField
              label={t("terminal.picker.passwordField")}
              type="password"
              autoComplete="off"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </>
      )}
    </form>
  );
}

/**
 * A server's own «Терминал» button (server page): connects at once where the
 * certificate sign-in worked, as in the picker; otherwise opens the picker
 * at that server's sign-in step.
 */
export function useOpenServerTerminal(projectId: string): (server: Server) => void {
  const hosts = useTerminalHosts(projectId);
  const start = useStart(projectId);
  return (server) => {
    const choice = pickerChoice(server, hosts.data?.hosts, rememberedUser);
    if (choice.kind === "direct") start(server, choice.unixUser, "certificate");
    else terminals.openPicker({ projectId, server });
  };
}

/**
 * "Connect to server": project → server (OS, status, addresses) → an SSH
 * terminal (Unix user and how to sign in) or the remote desktop (Windows
 * user and password; the default for Windows servers). A server whose
 * certificate sign-in is known to work connects on a click, as the
 * remembered user; its settings button opens the sign-in step. Projects
 * where the person is a guest are not offered.
 */
export function TerminalPicker({ request }: { request: PickerRequest }) {
  const { t } = useTranslation();
  const projects = useProjects();
  const allowed = (projects.data ?? []).filter((p) => p.access === "owner" || p.access === "admin");
  const [projectId, setProjectId] = useState(request.projectId ?? "");
  // Opened from a server (its page, list or dashboard): straight to signing in.
  const [server, setServer] = useState<Server | null>(
    request.server && !serverBlock(request.server) ? request.server : null,
  );
  const project = allowed.some((p) => p.id === projectId) ? projectId : (allowed[0]?.id ?? "");
  const hosts = useTerminalHosts(project, allowed.length > 0);
  const close = () => terminals.closePicker();
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && close()}
      title={t("terminal.picker.title")}
      description={t("terminal.picker.description")}
      footer={
        server ? (
          <Button type="submit" form="terminal-picker">
            {t("terminal.picker.submit")}
          </Button>
        ) : undefined
      }
    >
      {allowed.length === 0 ? (
        <p className="text-sm text-muted">{t("terminal.picker.noProjects")}</p>
      ) : server ? (
        <SignInStep
          key={server.id}
          projectId={project}
          server={server}
          workedAt={hosts.data?.hosts.find((h) => h.serverId === server.id)?.certificateWorkedAt}
          onBack={() => setServer(null)}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {allowed.length > 1 && (
            <Select
              label={t("terminal.picker.project")}
              value={project}
              onChange={(event) => setProjectId(event.target.value)}
            >
              {allowed.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
          {project && (
            <ServerList projectId={project} hosts={hosts.data?.hosts} onChoose={setServer} />
          )}
        </div>
      )}
    </Dialog>
  );
}
